import CameraCard from "@/Components/Cameras/CameraCard"
import StaticMap from "@/Components/Map/StaticMap"
import LatestImage from "@/Components/Media/LatestImage"
import CameraDetail, { CameraSummaryLoader } from "@/Pages/CameraDetail/CameraDetail"
import CamerasLoader from "@/Pages/Cameras/CamerasLoader"
import { CamerasTable } from "@/Pages/Cameras/CamerasView"
import { makeUTCDate } from "@/services/assets/parsers"
import { fetchTimeseriesAssetMedia, fetchWebCOOSCameraPageFiltered, type IWebCOOSCameraPageFiltered } from "@/services/assets/services"
import type { IWebCOOSAssetSummaryView, IWebCOOSElement, IWebCOOSElementInventory, IWebCOOSParsedAsset, IWebCOOSParsedGalleryService } from "@/services/assets/types"
import { useWebCOOSElementInventory } from "@/services/assets/useWebCOOSPostgrest"
import { useTimeSeriesAssetBrowse } from "@/services/media/useTimeSeriesAssetBrowse"
import ApiContext, { useAPIContext } from "@/state/ApiContext"
import { Button, SelectInput, Table, Tabs, ViewWithLoader } from "@axdspub/axiom-ui-utilities"
import { useQuery } from "@tanstack/react-query"
import { atom, useAtom } from "jotai"
import { Fragment, useContext, useEffect, useMemo, useRef, useState, type ReactElement } from "react"
import Markdown from "react-markdown"
import { Link, useParams } from "react-router"
import { Chart, EPlotTypes } from '@axdspub/axiom-charts'
import GalleryGrid from "@/Components/Media/GalleryGrid"
import headerStateAtom from "@/state/headerStateAtom"
import { CalendarDemo, DatePickerDemo } from "@/Components/DatePicker/DatePicker"
import ImageLoader from "@/Components/Media/ImageLoader"
import { InfiniteScrollList } from "@/Components/Media/InfiniteScroll"
import { VideoPlayer } from "@/Components/Media/VideoPlayer"
import { ExclamationCircleIcon } from "@heroicons/react/solid"

const urlParams = new URLSearchParams(window.location.search)
const initialCameraSlug = urlParams.get('camera_slug') ?? undefined
const selectedCameraSlugAtom = atom<string | undefined>(initialCameraSlug)


/* const binToNearestInterval = (dateInput: string, intervalSeconds: number): string => {
    const parsedDate = new Date(dateInput)
    const intervalMs = intervalSeconds * 1000
    if (Number.isNaN(parsedDate.getTime()) || intervalMs <= 0) {
        return dateInput
    }
    const roundedMs = Math.round(parsedDate.getTime() / intervalMs) * intervalMs
    return new Date(roundedMs).toISOString()
} */

const CameraPicker = ({ data, View }: { data: IWebCOOSCameraPageFiltered, View: React.FC<{ data: IWebCOOSCameraPageFiltered, camera: IWebCOOSAssetSummaryView }> }): ReactElement => {
    const urlParams = new URLSearchParams(window.location.search)
    const initialCameraSlug = urlParams.get('camera_slug') ?? undefined
    const [selectedSlug, setSelectedSlug] = useAtom(selectedCameraSlugAtom)
    const selectedCamera = data.assets.find(c => c.asset_slug === selectedSlug) ?? undefined
    const updateUrl = (slug: string | undefined) => {
        const newSearchParams = new URLSearchParams(window.location.search);
        if (slug !== undefined) {
            newSearchParams.set('camera_slug', slug);
        } else {
            newSearchParams.delete('camera_slug');
        }
        window.history.replaceState({}, '', `${window.location.pathname}?${newSearchParams.toString()}`);
    }
    useEffect(() => {
        if (selectedSlug !== initialCameraSlug) {
            setSelectedSlug(initialCameraSlug)
        } else if (selectedSlug !== undefined) {
            updateUrl(selectedSlug)
        }
    }, [initialCameraSlug, setSelectedSlug])

    return (
        <>
            <div className='flex flex-row gap-4 w-full relative'>
                <SelectInput
                    id='camera-select'
                    testId="camera-select"
                    value={selectedSlug}
                    onChange={e => {
                        const slug = e?.value !== undefined ? String(e.value) : undefined
                        updateUrl(slug)
                        setSelectedSlug(slug)
                    }}
                    options={data.assets.map(c => ({
                        label: c.asset_label,
                        value: c.asset_slug
                    }))}
                    placeholder="Select a camera"
                />
                <Button
                    type='none'
                    className={`p-0 border-0 bg-none text-2xl cursor-pointer ${selectedCamera === undefined ? 'invisible' : 'visible'}`}
                    onClick={() => {
                        updateUrl(undefined)
                        setSelectedSlug(undefined)
                    }}
                >
                    &times;
                </Button>
            </div>
            <div className='relative h-full'>
                {
                    selectedCamera === undefined
                        ? <p className="mt-4">Please select a camera to view its details.</p>
                        : <View data={data} camera={selectedCamera} />
                }
            </div>
        </>

    )
}


const ServiceSelector = ({
    data,
    View
}: {
    data: IWebCOOSCameraPageFiltered,
    View?: React.FC<{
        service: IWebCOOSParsedGalleryService | undefined,
        detail: IWebCOOSParsedAsset,
        summary: IWebCOOSAssetSummaryView,
    }>
}): ReactElement => <CameraPicker data={data} View={({ camera }): ReactElement => {
    return <CameraDetail slug={camera.asset_slug} View={({ detail, summary }): ReactElement => {
        const serviceByUUID: Record<string, typeof detail.galleryServices[0]> = Object.fromEntries(detail.galleryServices.map(s => [s.uuid, s]))
        return (
            <>{
                detail.galleryServices.length > 0
                    ? <>
                        <Tabs

                            tabs={detail.galleryServices.map(service => ({
                                id: service.uuid,
                                label: service.common.label,
                                content: View !== undefined
                                    ? <View detail={detail} summary={summary} service={serviceByUUID[service.uuid]} />
                                    : <>No service view provided</>
                            }))}
                        />
                    </>

                    : 'N/A'
            }</>
        )
    }} />
}}
    />

const MergedServicesLoader = ({
    camera,
    mergedServices,
    annotationImageService,
    stillsService
}: {
    camera: IWebCOOSAssetSummaryView
    mergedServices: IWebCOOSParsedGalleryService[],
    annotationImageService: IWebCOOSParsedGalleryService,
    stillsService: IWebCOOSParsedGalleryService
}): ReactElement => {

    const apiContext = useAPIContext()
    const serviceMap = Object.fromEntries(mergedServices.concat([annotationImageService].concat([stillsService])).map(s => [s.uuid, s]))
    const mergedServiceMap = Object.fromEntries(mergedServices.map(s => [s.uuid, s]))
    const [lookDirection, setLookDirection] = useState<'ahead' | 'behind' | 'both'>('behind')

    const mergedStartings = mergedServices.map(s => s.elements.first_starting ? new Date(s.elements.first_starting) : null).filter(d => d !== null).sort((a, b) => a.getTime() - b.getTime())
    const mergedEndings = mergedServices.map(s => s.elements.last_ending ? new Date(s.elements.last_ending) : new Date()).sort((a, b) => b.getTime() - a.getTime())


    const lastEnd = mergedEndings.length > 0 ? mergedEndings[0] : new Date()
    const firstStart = mergedStartings.length > 0 ? mergedStartings[0] : new Date(+lastEnd - 1000 * 60 * 60 * 24 * 7)

    const [mergedServiceTimeBinSeconds, setMergedServiceTimeBinSeconds] = useState(30)

    const [end, setEnd] = useState<Date | undefined>(lastEnd)

    const { data, isLoading, error } = useQuery({
        queryKey: ['merged-services-elements', end, mergedServices.map(s => s.uuid).join(','), annotationImageService.uuid, stillsService.uuid],
        queryFn: async ({ signal }) => {
            const results: Record<string, IWebCOOSElement[]> = {}
            await Promise.all(mergedServices.concat([annotationImageService].concat([stillsService])).map(async s => {
                const r = await fetchTimeseriesAssetMedia({
                    ...apiContext,
                    serviceIdentifier: s.uuid,
                    signal,
                    page: 1,
                    start: end ? new Date(+end - 60 * 60 * 1000 * 7) : undefined,
                    end,
                    //count: mergedServiceUuids.includes(s.uuid) ? imageCount : imageCount * mergedServices.length * 2
                })
                results[s.uuid] = r.results
                return r
            }))
            return results

        }
    })

    const { byDate, byService, statsByService } = useMemo(() => {
        if (!data) return { byDate: {}, byService: {} }
        const byDate: Record<string, Record<string, IWebCOOSElement[]>> = {}
        const byService: Record<string, IWebCOOSElement[]> = {}
        const stills = (data[stillsService.uuid] ?? []).map(d => {
            return {
                ...d,
                date: new Date(d.data.extents.temporal.min)
            }
        }).sort((a, b) => a.date.getTime() - b.date.getTime())
        const annotations = (data[annotationImageService.uuid] ?? []).map(d => {
            return {
                ...d,
                date: new Date(d.data.extents.temporal.min)
            }
        }).sort((a, b) => a.date.getTime() - b.date.getTime())
        Object.keys(data).forEach(serviceUuid => {
            if (mergedServiceMap[serviceUuid]) {
                data[serviceUuid].forEach(element => {


                    const nearbyStills = stills
                        .filter(s => {
                            const diff = s.date.getTime() - new Date(element.data.extents.temporal.min).getTime()
                            const absDiff = Math.abs(diff)
                            if (lookDirection === 'both') {
                                return absDiff < mergedServiceTimeBinSeconds * 1000
                            } else if (lookDirection === 'ahead') {
                                return diff > 0 && absDiff < mergedServiceTimeBinSeconds * 1000
                            } else if (lookDirection === 'behind') {
                                return diff < 0 && absDiff < mergedServiceTimeBinSeconds * 1000
                            }
                            return false
                        })
                        .map(e => {
                            return {
                                ...e,
                                serviceUuid: stillsService.uuid
                            }
                        })
                    const nearbyAnnotations = annotations
                        .filter(s => {
                            const diff = s.date.getTime() - new Date(element.data.extents.temporal.min).getTime()
                            const absDiff = Math.abs(diff)
                            if (lookDirection === 'both') {
                                return absDiff < mergedServiceTimeBinSeconds * 1000
                            } else if (lookDirection === 'ahead') {
                                return diff > 0 && absDiff < mergedServiceTimeBinSeconds * 1000
                            } else if (lookDirection === 'behind') {
                                return diff < 0 && absDiff < mergedServiceTimeBinSeconds * 1000
                            }
                            return false
                        })
                        .map(e => {
                            return {
                                ...e,
                                serviceUuid: annotationImageService.uuid
                            }
                        })

                    const date = element.data.extents.temporal.min

                    if (byDate[date] === undefined) {
                        byDate[date] = {}
                    }

                    [element].concat(nearbyStills).concat(nearbyAnnotations).forEach((e: IWebCOOSElement & { serviceUuid?: string }) => {
                        const _sv = e.serviceUuid ?? serviceUuid
                        byDate[date][_sv] = byDate[date][_sv] ?? []
                        if (!byDate[date][_sv].find(existing => existing.uuid === e.uuid)) {
                            byDate[date][_sv].push(e)
                        }
                        byService[_sv] = byService[_sv] ?? []
                        if (!byService[_sv].find(existing => existing.uuid === e.uuid)) {
                            byService[_sv].push(e)
                        }
                    })






                    /* const date = binToNearestInterval(
                        element.data.extents.temporal.min,
                        mergedServiceTimeBinSeconds
                    )
                    if (byDate[date] === undefined) {
                        byDate[date] = {}
                    }
                    byDate[date][serviceUuid] = byDate[date][serviceUuid] ?? []
                    byDate[date][serviceUuid].push(element)
                    byService[serviceUuid] = byService[serviceUuid] ?? []
                    byService[serviceUuid].push(element) */
                })
            }
        })
        const statsByService = Object.fromEntries(Object.keys(byService).map(serviceUuid => {
            const elements = byService[serviceUuid]
            const count = elements.length
            const dates = elements.map(e => new Date(e.data.extents.temporal.min)).sort((a, b) => a.getTime() - b.getTime())
            const start = dates[0]
            const end = dates[dates.length - 1]
            return [serviceUuid, { count, start, end }]
        }))
        return { byDate, byService, statsByService }
    }, [data, mergedServiceTimeBinSeconds, lookDirection])

    const dateRef = useRef<HTMLInputElement | null>(null)


    return (
        <ViewWithLoader data={data} isLoading={isLoading} error={error}>
            <div className='flex flex-col gap-4'>
                <div className='flex flex-row gap-8 text-xs bg-slate-100 p-2'>
                    <div>
                        <SelectInput
                            id='time-bin-select'
                            size='xs'
                            testId='time-bin-select'
                            label="Time to scan from detection (seconds)"
                            value={mergedServiceTimeBinSeconds}
                            className="bg-white"
                            onChange={e => {
                                if (e?.value !== undefined) {
                                    setMergedServiceTimeBinSeconds(Number(e.value))
                                }
                            }}
                            options={[0, 5, 10, 15, 20, 25, 30, 40, 50, 60, 120, 180, 240, 300, 360].map(s => ({ label: s.toString(), value: s }))}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label>End time (loads 1 week prior up to end)</label>
                        <input
                            ref={dateRef}
                            type='date'
                            className='border rounded-md p-1.5 text-xs bg-white'
                            value={end ? new Date(end).toISOString().split('T')[0] : ''}
                            onChange={(e) => {
                                const newEnd = e.target.value ? new Date(e.target.value) : undefined
                                if (newEnd !== undefined) {
                                    setEnd(newEnd)
                                }
                            }}
                            min={firstStart.toISOString().split('T')[0]}
                            max={lastEnd.toISOString().split('T')[0]}
                        />
                    </div>
                    <div>
                        <SelectInput
                            id='look-direction-select'
                            testId='look-direction-select'
                            size='xs'
                            label="Scan relative to detection record"
                            value={lookDirection}
                            className='bg-white'
                            options={
                                [
                                    {
                                        value: 'both',
                                        label: 'Both'
                                    },
                                    {
                                        value: 'ahead',
                                        label: 'Ahead',
                                    },
                                    {
                                        value: 'behind',
                                        label: 'Behind'
                                    }
                                ]
                            }
                            onChange={(e) => {
                                setLookDirection((e?.value ?? 'both') as 'ahead' | 'behind' | 'both')
                            }}
                        />
                    </div>

                </div>
                {
                    data && byDate && byService && statsByService && <>
                        <table>
                            <thead className='sticky top-15 bg-white z-20'>
                                <tr>
                                    <th key={'date'} className='px-4 py-2 border'>Date</th>
                                    {
                                        Object.keys(serviceMap).map(serviceUuid => (
                                            <th key={serviceUuid} className={`px-4 py-2 border${!mergedServiceMap[serviceUuid] ? ' w-[197px]' : ''}`}><span className='flex flex-row gap-1 items-center'>{serviceMap[serviceUuid].common.label} <span className='bg-slate-600 text-white p-1 rounded-sm text-xs font-normal'>{statsByService[serviceUuid]?.count ?? 0}</span> </span></th>
                                        ))
                                    }
                                </tr>
                            </thead>
                            <tbody className='text-xs'>
                                {
                                    Object.keys(byDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()).map(date => (

                                        <tr key={date} className='odd:bg-slate-100 hover:border-b-2'>
                                            <td className='px-4 py-2 border align-top font-bold'>{date}</td>
                                            {
                                                Object.keys(serviceMap).map(serviceUuid => (
                                                    <td key={serviceUuid} className={`px-4 align-top py-2 border${new Date(date).getTime() >= (statsByService[serviceUuid]?.start?.getTime() - mergedServiceTimeBinSeconds * 1000) ? ' bg-green-100' : ''}`}>
                                                        {byDate[date][serviceUuid]
                                                            ? <div className="relative w-full h-full">
                                                                <span className='bg-slate-600 text-white  p-2 py-1 font-bold rounded-md m-2 absolute right-0 top-0'>{byDate[date][serviceUuid].length}</span>
                                                                <div className='flex flex-col gap-2'>{byDate[date][serviceUuid].map(e => {
                                                                    return (
                                                                        <Link to={`/cameras/${camera.asset_slug}/service/${serviceUuid}/media/${e.uuid}`} className="text-blue-600" key={e.uuid}>{
                                                                            !mergedServiceMap[serviceUuid]
                                                                                ? e.data?.properties?.thumbnails?.base?.rect_small !== undefined
                                                                                    ? <div>
                                                                                        <img src={e.data.properties.thumbnails.base.rect_small} width={177} height={100} className='object-none' loading="lazy" />

                                                                                    </div>
                                                                                    : <div className='w-[177px] h-[100px] bg-gray-300 flex items-center justify-center text-gray-500'>No thumbnail</div>
                                                                                : e.uuid
                                                                        }
                                                                            <p className='text-slate-600 text-[10px]'>{e.data.extents.temporal.min}</p>
                                                                        </Link>

                                                                    )
                                                                })}
                                                                </div>
                                                            </div>
                                                            : 'N/A'
                                                        }
                                                    </td>
                                                ))
                                            }
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </>
                }
            </div>
        </ViewWithLoader>
    )




}

const ServiceMetadataWithLink = ({ service, camera }: { service: IWebCOOSParsedGalleryService, camera: IWebCOOSAssetSummaryView }): ReactElement => {
    return (
        <p><Link to={`/cameras/${camera.asset_slug}?service=${service.uuid}`} className='text-blue-600'>{service.common.label}</Link> ({service.slug}) : {service.elements.first_starting} to {service.elements.last_ending}</p>
    )
}

const ServiceSelectorMergeGalleries = ({
    data
}: {
    data: IWebCOOSCameraPageFiltered
}): ReactElement => {
    return <CameraPicker data={data} View={({ camera }): ReactElement => <CameraDetail slug={camera.asset_slug} View={({ detail }): ReactElement => {
        const annotationImageServices = detail.galleryServices.filter(s => s.common.slug.match(/annotated-image/))
        const annotatedImageService = annotationImageServices.length === 1 ? annotationImageServices[0] : null
        const stillsServices = detail.galleryServices.filter(s => s.common.slug.match(/-stills/))
        const stillsService = stillsServices.length === 1 ? stillsServices[0] : null
        const mergedServices = detail.galleryServices.filter(s => s.common.slug.match(/detection-result/))

        return (
            <>
                {
                    annotationImageServices.length > 1 && <div className="flex flex-row items-center gap-2"><ExclamationCircleIcon className='w-4 h-4' /> Multiple annotation image services found</div>
                }
                {
                    annotationImageServices.length === 0 && <div className="flex flex-row items-center gap-2"><ExclamationCircleIcon className='w-4 h-4' /> No annotation image services found</div>
                }
                {
                    mergedServices.length === 0 && <div className="flex flex-row items-center gap-2"><ExclamationCircleIcon className='w-4 h-4' /> No detection result services found</div>
                }
                {
                    stillsServices.length > 1 && <div className="flex flex-row items-center gap-2"><ExclamationCircleIcon className='w-4 h-4' /> Multiple stills services found</div>
                }
                {
                    stillsServices.length === 0 && <div className="flex flex-row items-center gap-2"><ExclamationCircleIcon className='w-4 h-4' /> No stills services found</div>
                }
                <div className="flex flex-col gap-4"></div>
                <div>
                    <p className='font-bold'>All services</p>
                    <div>
                        {
                            detail.galleryServices.map(service => (
                                <div key={service.uuid} className='p-4 odd:bg-slate-100'>
                                    <ServiceMetadataWithLink service={service} camera={camera} />
                                </div>
                            ))
                        }
                    </div>
                </div>
                {
                    annotatedImageService && stillsService && mergedServices.length > 0 &&
                    (
                        <>
                            <div>
                                <p className='font-bold'>Merged services</p>
                                <div>
                                    {
                                        mergedServices.map(service => (
                                            <div key={service.uuid} className='p-4 odd:bg-slate-100'>
                                                <ServiceMetadataWithLink service={service} camera={camera} />
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                            <div>
                                <p className='font-bold'>Annotation image service</p>
                                <div className='p-4 odd:bg-slate-100'>
                                    <ServiceMetadataWithLink service={annotatedImageService} camera={camera} />
                                </div>
                            </div>
                            <div>
                                <p className='font-bold'>Stills image service</p>
                                <div className='p-4 odd:bg-slate-100'>
                                    <ServiceMetadataWithLink service={stillsService} camera={camera} />
                                </div>
                            </div>
                            <MergedServicesLoader camera={camera} mergedServices={mergedServices} annotationImageService={annotatedImageService} stillsService={stillsService} />
                        </>
                    )

                }

            </>
        )
    }} />} />
}





const GalleryBrowse = ({ service }: { service?: IWebCOOSParsedGalleryService }): ReactElement => {
    const apiContext = useAPIContext()
    const startString = service?.elements.first_starting ?? service?.elements.first_ending ?? null
    const endString = service?.elements.last_ending ?? service?.elements.last_starting ?? null
    const start = startString !== null ? makeUTCDate(startString) : undefined
    const end = endString !== null ? makeUTCDate(endString) : undefined
    const [currentDate, setCurrentDate] = useState<Date | undefined>(undefined)
    const [direction, setDirection] = useState<'previous' | 'next' | 'nearest' | 'first' | 'last'>('first')
    const { data, isLoading, isFetching, error } = useTimeSeriesAssetBrowse({
        serviceIdentifier: service?.uuid ?? '',
        ...apiContext,
        date: currentDate ?? start ?? makeUTCDate(),
        direction,
        enabled: service !== undefined && (currentDate !== undefined || start !== undefined)
    })

    return (
        <>
            {
                start !== undefined && end !== undefined && <div className='flex flex-col gap-4'>
                    <p>{startString} to {endString}</p>
                    <div className='p-4 bg-slate-100 shadow-md relative'>
                        <ViewWithLoader data={data} isLoading={isLoading} isFetching={isFetching} error={error} keepExistingContentWhileLoading={true}>
                            {
                                (data as { message?: string })?.message !== undefined
                                    ? <p>{(data as { message: string }).message}</p>
                                    :
                                    <>
                                        <p>{(data as IWebCOOSElement)?.data.extents.temporal.min ?? 'NAN'}</p>
                                        <ImageLoader url={(data as IWebCOOSElement)?.data.properties.url ?? ''} alt="Current element thumbnail" className="shadow-md border border-gray-400" />
                                    </>
                            }
                        </ViewWithLoader>
                    </div>
                    {
                        (data as { message?: string })?.message === undefined &&
                        <div className='flex flex-row gap-4 items-center'>
                            <Button
                                type='create'
                                disabled={data === undefined || data === null || new Date((data as IWebCOOSElement)?.data.extents.temporal.min).getTime() === +start}
                                onClick={() => {
                                    setDirection('first')
                                    if (data !== undefined && data !== null) {
                                        if (data !== undefined) {
                                            setCurrentDate(makeUTCDate((data as IWebCOOSElement).data.extents.temporal.min))
                                        }
                                    }
                                }}
                            >First</Button>
                            <Button
                                type='create'
                                disabled={data === undefined || data === null || new Date((data as IWebCOOSElement)?.data.extents.temporal.min).getTime() === +start}
                                onClick={() => {
                                    if (data !== undefined && data !== null) {
                                        setDirection('previous')
                                        if (data !== undefined) {
                                            setCurrentDate(makeUTCDate((data as IWebCOOSElement).data.extents.temporal.min))
                                        }
                                    }
                                }}
                            >Previous</Button>
                            <Button
                                type='create'
                                disabled={data === undefined || data === null || new Date((data as IWebCOOSElement)?.data.extents.temporal.min).getTime() === +end}
                                onClick={() => {
                                    if (data !== undefined && data !== null) {
                                        setDirection('next')
                                        if (data !== undefined) {
                                            setCurrentDate(makeUTCDate((data as IWebCOOSElement).data.extents.temporal.min))
                                        }

                                    }
                                }}
                            >Next</Button>
                            <Button
                                type='create'
                                disabled={data === undefined || data === null || new Date((data as IWebCOOSElement)?.data.extents.temporal.min).getTime() === +end}
                                onClick={() => {
                                    setDirection('last')
                                    if (data !== undefined && data !== null) {
                                        if (data !== undefined) {
                                            setCurrentDate(makeUTCDate((data as IWebCOOSElement).data.extents.temporal.min))
                                        }
                                    }
                                }}
                            >Last</Button>
                        </div>
                    }
                </div>
            }
        </>
    )
}


const InventoryPlot = ({ data, timeDomain, fill }: { uuid: string, data: IWebCOOSElementInventory[], timeDomain?: Date[], fill?: string }): ReactElement => {

    const dates: Date[] = data.map(d => new Date(d.time_bucket)).sort((a, b) => a.getTime() - b.getTime())
    const segmentSize = {
        hour: 60 * 60 * 1000,
        day: 24 * 60 * 60 * 1000,
        month: 30 * 24 * 60 * 60 * 1000,
        year: 365 * 24 * 60 * 60 * 1000,
        week: 7 * 24 * 60 * 60 * 1000
    }[data[0].bucket_grouping || 'day'] ?? 0
    const minDate = new Date((timeDomain?.[0] !== undefined ? timeDomain[0].getTime() : dates[0].getTime()) - segmentSize)
    const maxDate = new Date((timeDomain?.[1] !== undefined ? timeDomain[1].getTime() : dates[dates.length - 1].getTime()) + segmentSize)
    const max = Math.max(...data.map(d => d.bucket_count))
    const min = Math.min(...data.map(d => d.bucket_count))
    const span = max - min
    const buffer = span * 0.1
    const chartMin = min - buffer < 0 ? 0 : min - buffer
    const chartMax = max + buffer
    return (
        <>
            <Chart
                settings={{
                    margin: { top: 20, right: 20, bottom: 50, left: 20 },
                    width: 'auto',
                    height: 100,
                    axes: {
                        y: {
                            exclude: true,
                            domain: [
                                chartMin,
                                chartMax,
                            ]
                        },
                        x: {
                            /* ticks: 6,
                            tickFormat: (d: Date) => {
                                return `${String(d.getUTCFullYear()).padStart(4, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`
                            }, */
                            domain: [
                                minDate,
                                maxDate

                            ]
                        }
                    }

                }}
                plots={[
                    {
                        id: `webcoos-inventory`,
                        type: EPlotTypes.bar,
                        dataService: {
                            type: 'preloaded',
                            url: 'preloaded',
                            result: {
                                data,
                                parsed: {
                                    data,
                                    accessors: {
                                        x: d => new Date(d.time_bucket),
                                        y: d => d.bucket_count,
                                        y1: d => d.bucket_count,
                                        y0: () => chartMin,
                                        fill: (d => fill ?? d.bucket_count > 2000 ? '#d62728' : '#1f77b4'),
                                        size: function () {
                                            return 2
                                        }
                                    },
                                    args: {}
                                }
                            }
                        },
                        style: {
                            fill: '#1f77b4',
                        },
                        dimensions: {
                            x: {
                                property: 'time_bucket',
                                parameter: 'time_bucket'
                            },
                            y: {
                                property: 'bucket_count',
                                parameter: 'bucket_count'
                            }
                        }

                    }
                ]}

            />


        </>

    )

}


const ServiceInventory = ({ service, timeDomain }: { service: IWebCOOSParsedGalleryService, timeDomain?: Date[] }): ReactElement => {
    const apiContext = useAPIContext()
    const { data, isLoading, isFetching, error } = useWebCOOSElementInventory({
        ...apiContext,
        params: {
            filters: [
                {
                    column: 'service_slug',
                    operator: 'eq',
                    value: service.common.slug
                }
            ],
            order: [
                {
                    column: 'time_bucket',
                    dir: 'desc'
                }
            ]
        },
        grouping: 'week'

    })

    return (
        <ViewWithLoader data={data} isLoading={isLoading} isFetching={isFetching} error={error}>
            {
                data && data.length > 0 &&
                <>
                    <InventoryPlot uuid={service.uuid} data={data} timeDomain={timeDomain} />
                    {/* <Table
                        columns={[
                            {
                                id: 'time_bucket',
                                label: 'Date'
                            },
                            {
                                id: 'bucket_count',
                                label: 'Bucket Count'
                            }
                        ]}
                        data={data}
                    /> */}
                </>
            }
        </ViewWithLoader >
    )
}
const groups = [
    {
        label: 'Camera lists',
        id: 'camera-lists',
        items: [
            {
                label: 'Table',
                id: 'table',
                content: ({ }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {



                    /* return <CamerasTable data={data} theadClassName="top-16" className='-mt-10' /> */
                    return <CamerasLoader View={({ data }): ReactElement => <CamerasTable data={data} theadClassName="top-16" className='-mt-10' />} />

                },
            },
            {
                label: 'Map',
                id: 'map',
                content: ({ }: { data: IWebCOOSCameraPageFiltered }): ReactElement => <>MAP</>,
            },
            {
                label: 'Inventory',
                id: 'inventory',
                content: ({ }: { data: IWebCOOSCameraPageFiltered }): ReactElement => <>INVENTORY</>,
            }
        ]
    },
    {
        label: 'Camera detail',
        id: 'camera-detail',
        items: [
            {
                label: 'Card',
                id: 'card',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => <CameraPicker data={data} View={({ camera }): ReactElement => <div className='w-[400px] rounded-md shadow-2xl overflow-hidden min-h-[300px] relative'>
                    <CameraCard slug={camera.asset_slug} />
                </div>} />,
            },
            {
                label: 'Map',
                id: 'map',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => <CameraPicker data={data} View={({ camera }): ReactElement => {


                    return <div className='relative w-[600px] h-[400px] shadow-md'><CameraDetail slug={camera.asset_slug} View={({ summary }): ReactElement => {
                        const point = summary.asset_data.properties.location as GeoJSON.Point | null
                        return (<>{
                            point !== null
                                ? <StaticMap
                                    latitude={point.coordinates[1]}
                                    longitude={point.coordinates[0]}
                                    wedgePolygon={summary.asset_data.properties.wedge ?? undefined}
                                    width={600}
                                    height={400}
                                    zoom={10}
                                />
                                : <p>No location data available</p>
                        }</>)
                    }} />
                    </div>
                }} />

            },
            {
                label: 'Streaming video',
                id: 'streaming-video',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => <CameraPicker data={data} View={({ camera }): ReactElement => {
                    return <CameraDetail slug={camera.asset_slug} View={({ summary, liveStream }): ReactElement => {
                        return (
                            liveStream
                                ? <div className='h-[600px] bg-slate-100'>
                                    <VideoPlayer options={{
                                        sources: [{
                                            src: liveStream.url
                                        }],
                                        autoPlay: true,
                                        controls: true,
                                        responsive: true,
                                        fluid: true,
                                        muted: true
                                    }} /></div>
                                : <>No live stream available for {summary.asset_label}</>
                        )
                    }} />
                }} />
            },
            {
                label: 'Details',
                id: 'details',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => <CameraPicker data={data} View={({ camera }): ReactElement => {
                    return <CameraSummaryLoader slug={camera.asset_slug} View={({ summary }): ReactElement => {
                        return (
                            <Table
                                rowClassName="even:bg-slate-50 odd:bg-slate-200"
                                columns={[
                                    {
                                        id: 'label',
                                        label: 'Label',
                                        cellClassName: 'font-bold align-top border-r-2 border-slate-300 border-b-0',
                                    },
                                    {
                                        id: 'value',
                                        label: 'Value', accessor: (row) => <div className="wrap-break-word">{row.value}</div>
                                    }
                                ]}
                                data={[
                                    {
                                        label: 'label',
                                        value: summary.asset_label
                                    },
                                    {
                                        label: 'slug',
                                        value: summary.asset_slug
                                    },
                                    {
                                        label: 'description',
                                        value: <Markdown>{summary.asset_description}</Markdown>
                                    },
                                    {
                                        label: 'lat/lon',
                                        value: summary.asset_data.properties.location !== null
                                            ? `${(summary.asset_data.properties.location as GeoJSON.Point).coordinates[1]}, ${(summary.asset_data.properties.location as GeoJSON.Point).coordinates[0]}`
                                            : 'N/A'
                                    },
                                    {
                                        label: 'start/end date',
                                        value: summary.asset_first_starting && summary.asset_last_ending
                                            ? `${makeUTCDate(summary.asset_first_starting).toLocaleDateString()} to ${makeUTCDate(summary.asset_last_ending).toLocaleDateString()}`
                                            : 'N/A'
                                    },
                                    {
                                        label: 'element size',
                                        value: summary.asset_element_size
                                    },
                                    {
                                        label: 'element count',
                                        value: summary.asset_element_count
                                    },
                                    {
                                        label: 'package',
                                        value: summary.package_label
                                    },
                                    {
                                        label: 'package group',
                                        value: summary.package_group
                                    },
                                    {
                                        label: 'package timezone',
                                        value: summary.package_timezone
                                    },
                                    {
                                        label: 'country',
                                        value: summary.asset_country
                                    },
                                    {
                                        label: 'state',
                                        value: summary.asset_state_or_territory
                                    },
                                    {
                                        label: 'operational status',
                                        value: summary.asset_operational_status_label
                                    },
                                    {
                                        label: 'disposition',
                                        value: summary.asset_disposition_label
                                    },
                                    {
                                        label: 'kind',
                                        value: summary.asset_data.kind
                                    },
                                    {

                                    }
                                ]}
                            />
                        )
                    }} />
                }} />
            },
            {
                label: 'Latest',
                id: 'latest',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => <CameraPicker data={data} View={({ camera }): ReactElement => <div className='relative'>
                    <CameraDetail slug={camera.asset_slug} View={({ detail, summary }): ReactElement => <>{
                        detail.stillImageService !== null
                            ? <LatestImage
                                asset={summary}
                                service={detail.stillImageService}
                                assetLabel={detail.label}
                                className='shadow-md border-gray-400 min-h-[400px]'
                                serviceSlug={detail.stillImageService.data.common.slug}
                            />
                            : 'N/A'
                    }</>} />


                </div>} />,
            },
            {
                label: 'Thumb image',
                id: 'thumb-image',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => <CameraPicker data={data} View={({ camera }): ReactElement => {
                    return <CameraSummaryLoader slug={camera.asset_slug} View={({ summary }): ReactElement => {
                        return (
                            <>{
                                summary.asset_data.properties.thumbnails?.base !== null && summary.asset_data.properties.thumbnails?.base !== undefined
                                    ? <div className='flex flex-col gap-4'>
                                        {
                                            ['rect_large', 'rect_medium', 'rect_small', 'square_large', 'square_medium', 'square_small'].map(sizeKey => {
                                                const imgUrl = (summary.asset_data.properties.thumbnails!.base as Record<string, string>)[sizeKey]
                                                const image = imgUrl !== undefined ? <div className=''><ImageLoader key={sizeKey} url={imgUrl} label={`${summary.asset_label} - ${sizeKey.replace('_', ' ')}`} className="shadow-md border border-gray-400" /><p>{sizeKey}</p></div> : null
                                                return <Fragment key={sizeKey}>{image}</Fragment>
                                            })
                                        }
                                    </div>
                                    : 'N/A'
                            }</>
                        )
                    }} />
                }} />,
            },
            {
                label: 'Gallery grid',
                id: 'gallery-grid',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {

                    return <ServiceSelector data={data} View={({ service, summary }) => {
                        const end = makeUTCDate(service?.elements.last_ending ?? service?.elements.last_starting ?? makeUTCDate())
                        const start = makeUTCDate(+end - 24 * 60 * 60 * 1000)
                        return (
                            service
                                ? <GalleryGrid service={service} summary={summary} start={start} end={end} />
                                : <>No service selected</>
                        )
                    }} />
                },
            },
            {
                label: 'Infinite scroll gallery',
                id: 'infinite-scroll-gallery',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {

                    return <ServiceSelector data={data} View={({ service, summary }) => {
                        const end = makeUTCDate(service?.elements.last_ending ?? service?.elements.last_starting ?? makeUTCDate())
                        const start = makeUTCDate(+end - 24 * 60 * 60 * 1000)
                        return (
                            service
                                ? <InfiniteScrollList service={service} start={start} end={end} asset={summary} />
                                : <>No service selected</>
                        )
                    }} />
                },

            },
            {
                label: 'Gallery browse',
                id: 'gallery-browse',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {
                    return <ServiceSelector data={data} View={GalleryBrowse} />
                }
            },
            {
                label: 'Inventory',
                id: 'inventory',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {

                    return <CameraPicker data={data}
                        View={
                            ({ camera }): ReactElement => {
                                return <CameraDetail slug={camera.asset_slug} View={({ detail }): ReactElement => {
                                    return (
                                        <div className='flex flex-col gap-4'>
                                            <p className="text-xl font-bold">{
                                                detail.dateBounds.map(d => d?.toString() ?? 'N/A').join(' to ')
                                            }</p>
                                            {
                                                detail.galleryServices.length > 0
                                                    ? detail.galleryServices.map(service => (
                                                        <div key={service.uuid} className='border-t border-slate-300 pt-4'>
                                                            <p>{service.common.label} ({service.uuid})</p>
                                                            <ServiceInventory key={service.uuid} service={service} timeDomain={detail.timeDomain} />
                                                        </div>
                                                    ))
                                                    : 'N/A'
                                            }
                                        </div>
                                    )
                                }} />
                            }
                        }
                    />
                }
            },
            {
                label: 'Gallery merge?',
                id: 'gallery-merge',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {
                    return <ServiceSelectorMergeGalleries data={data} />
                }
            }
        ]
    },
    {
        label: 'Components',
        id: 'components',
        items: [
            {
                id: 'datepicker',
                label: 'Date picker',
                content: ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {
                    return <ServiceSelector data={data} View={({ service }) => {
                        const apiContext = useAPIContext()
                        const { data, isLoading, isFetching, error } = useWebCOOSElementInventory({
                            ...apiContext,
                            params: {
                                filters: [
                                    {
                                        column: 'service_slug',
                                        operator: 'eq',
                                        value: service?.common.slug ?? 'NA'
                                    }
                                ],
                                order: [
                                    {
                                        column: 'time_bucket',
                                        dir: 'desc'
                                    }
                                ]
                            },
                            grouping: 'day'
                        })

                        const byMonth = data !== undefined
                            ? Object.fromEntries(Object.entries(data.reduce((acc, curr) => {
                                const date = new Date(curr.time_bucket)
                                const monthKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
                                if (!acc[monthKey]) {
                                    acc[monthKey] = []
                                }
                                acc[monthKey].push(curr)
                                return acc
                            }
                                , {} as Record<string, IWebCOOSElementInventory[]>)).sort())
                            : {}
                        return (
                            <ViewWithLoader data={data} isLoading={isLoading} isFetching={isFetching} error={error}>
                                {data && data.length > 0 &&
                                    <>
                                        <DatePickerDemo
                                            selectedDate={service?.elements.last_starting ? new Date(service.elements.last_starting) : undefined}
                                            inventoryData={data}

                                        />
                                        <div className='grid grid-cols-3 gap-4 mt-4'>
                                            {Object.keys(byMonth).map(monthKey => (
                                                <div key={monthKey} className='border border-slate-300 rounded-md p-2'>
                                                    <CalendarDemo
                                                        className='border-none shadow-none mx-auto'
                                                        selectedDate={undefined}
                                                        setSelectedDate={() => { }}
                                                        inventoryData={byMonth[monthKey]}
                                                        start={new Date(`${monthKey}-01`)}
                                                        end={new Date(`${monthKey}-31`)}
                                                        calendarProps={{
                                                            captionLayout: 'label',
                                                            hideNavigation: true,
                                                            showOutsideDays: false
                                                        }}
                                                    />
                                                    <div className='hidden'>
                                                        <InventoryPlot
                                                            uuid={service?.uuid ?? ''}
                                                            data={byMonth[monthKey]}
                                                            fill="#1f77b4"
                                                            timeDomain={[
                                                                new Date(`${monthKey}-01`),
                                                                new Date(`${monthKey}-31`)
                                                            ]}
                                                        />
                                                    </div>

                                                </div>
                                            ))}
                                        </div>
                                    </>
                                }
                            </ViewWithLoader>
                        )
                    }} />
                },
            }
        ]
    }
]

const mapped = Object.fromEntries(groups.map(g => [g.id, Object.fromEntries(g.items.map(i => [i.id, { ...i, group: g }]))]))




const Demo = (): ReactElement => {
    const [headerState, setHeaderState] = useAtom(headerStateAtom)
    const apiContext = useContext(ApiContext)

    useEffect(() => {
        const prevHeaderState = headerState
        setHeaderState(true)
        return () => {
            setHeaderState(prevHeaderState)
        }

    }, [])

    const { data, isLoading, isFetching, error } = useQuery<IWebCOOSCameraPageFiltered>({
        queryKey: ['webcoos', 'assets', 'summary'],
        queryFn: async ({ signal }) => {
            const results = await fetchWebCOOSCameraPageFiltered({
                apiUrl: apiContext.apiUrl,
                apiVersion: 'v1',
                source: 'webcoos',
                token: apiContext.token,
                signal
            })
            return results
        },
        placeholderData: (previousData): IWebCOOSCameraPageFiltered | undefined => {
            return previousData ?? undefined
        }
    })

    const { groupId, itemId } = useParams<{ groupId: string; itemId: string }>()
    const selected = mapped[groupId!]?.[itemId!]

    return (
        <ViewWithLoader isLoading={isLoading} isFetching={isFetching} error={error} data={data}>
            {
                data !== undefined &&

                <div className='absolute top-0 left-0 w-full h-full flex items-center flex-row'>
                    <div className='h-full bg-slate-100 overflow-y-auto flex flex-col gap-4 w-[20%] min-w-[200px] shadow-2xl z-50'>
                        {groups.map((group) => (
                            <div key={group.id} className='border-b border-slate-300'>
                                <h3 className='font-bold mb-2 p-2'>{group.label}</h3>
                                <ul className='flex flex-col'>
                                    {group.items.map((item) => {
                                        const selected = groupId === group.id && itemId === item.id
                                        return (<li key={item.id}>
                                            <Link to={`/demo/${group.id}/${item.id}`} className={`block p-2 text-primary ${selected ? 'bg-primary text-white' : 'hover:bg-blue-100'}`}>{item.label}</Link>
                                        </li>
                                        )
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                    <div className='h-full w-1/2 overflow-y-auto grow relative'>
                        {
                            selected
                                ? <>
                                    <h2 className='text-2xl font-bold bg-white sticky z-10 shadow-2xl top-0 p-10 py-4 flex flex-row justify-between items-center gap-4'>
                                        <span>
                                            <span className='text-slate-400'>
                                                {selected.group.label} &raquo;&nbsp;
                                            </span>
                                            {selected.label}
                                        </span>
                                        <Link to="/demo" className="text-slate-600 hover:text-slate-900">&times;</Link>
                                    </h2>
                                    <div className='p-10 flex flex-col gap-4'>
                                        {selected.content({ data })}

                                    </div>
                                </>
                                : <div className='flex items-center justify-center h-full'>
                                    <h2 className='text-2xl font-bold'>Please select a demo from the left menu</h2>
                                </div>

                        }

                    </div>

                </div>
            }
        </ViewWithLoader>
    )
}

export default Demo